import {
  ApplicationStage,
  ApplicationStatus as ApplicationStatusTypes,
  IApplicationValidation,
} from '@/types/application';
import { AccessTime, Cancel, Check } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import { useApplicationValidationStatusText } from '../../hooks/useApplicationValidationStatusText';

type ApplicationStatusProps = {
  status: ApplicationStatusTypes;
  validations?: IApplicationValidation[];
  stage?: ApplicationStage;
};

const getStatusInfo = (
  stage?: ApplicationStage,
): {
  [key in ApplicationStatusTypes]: {
    bgColor: string;
    label: string;
    tooltip?: string;
    icon?: React.ReactElement;
  };
} => ({
  [ApplicationStatusTypes.Applied]: {
    bgColor: 'bg-mydra-violet',
    label: 'Applied',
    icon: <Check sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.PendingValidation]: {
    bgColor: 'bg-mydra-yellow',
    label:
      stage === ApplicationStage.InProgress
        ? 'Awaiting validation'
        : 'Pending validation',
    icon: <AccessTime sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.Approved]: {
    bgColor: 'bg-mydra-blue',
    label: 'Approved',
    icon: <Check sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.Cancelled]: {
    bgColor: 'bg-mydra-medium-gray',
    label: 'Cancelled',
    icon: <Cancel sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.Rejected]: {
    bgColor: 'bg-mydra-light-pink',
    label: 'Rejected',
    icon: <Cancel sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.MissingScoringData]: {
    bgColor: 'bg-mydra-light-orange',
    label: 'Missing scoring data',
    icon: <AccessTime sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.PendingContract]: {
    bgColor: 'bg-mydra-medium-purple',
    label: 'Pending contract',
    tooltip: 'The user must sign their contract',
    icon: <AccessTime sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.ContractSigned]: {
    bgColor: 'bg-mydra-light-blue',
    label: 'Contract signed',
    icon: <Check sx={{ fontSize: 16 }} />,
  },
  [ApplicationStatusTypes.Complete]: {
    bgColor: 'bg-mydra-medium-green',
    label:
      stage === ApplicationStage.InProgress ? 'Awaiting Payment' : 'Completed',
    icon: <Check sx={{ fontSize: 16 }} />,
  },
});

export function ApplicationStatusChip({
  status,
  stage,
  validations,
}: ApplicationStatusProps) {
  const { getStatusText } = useApplicationValidationStatusText();

  const tooltip =
    status === ApplicationStatusTypes.PendingValidation &&
    Array.isArray(validations) &&
    validations?.length > 0
      ? getStatusText(validations)
      : getStatusInfo(stage)?.[status]?.tooltip;

  const statusData = getStatusInfo(stage)?.[status];
  const hasTooltip = !!tooltip;

  const chipContent = (
    <Chip
      label={
        <div className="flex gap-1 text-mydra-black items-center">
          <span className="break-words whitespace-normal">
            {statusData?.label ?? ''}
          </span>
          {statusData?.icon && (
            <span className="text-[#5F6368] flex items-center self-start h-6">
              {statusData.icon}
            </span>
          )}
        </div>
      }
      className={`text-wrap h-auto rounded-sm ${statusData?.bgColor ?? ''}`}
      size="small"
      variant="filled"
    />
  );

  if (hasTooltip) {
    return (
      <Tooltip title={tooltip} arrow>
        {chipContent}
      </Tooltip>
    );
  }

  return chipContent;
}
